// --- React
import { useCallback, useMemo, useState } from "react";
// --- Config & Utils
import * as columnDefs from "../../../../../components/AGGrid/config/columns-def";
// --- Hooks
import { usePMXDeliveriesData } from "../../../hooks/usePMXDeliveriesData";

/**
 * Custom hook for PMX Deliveries grid utilities.
 *
 * Features:
 * - Manages sorting state and logic for the PMX deliveries grid
 * - Fetches sorted PMX deliveries data from the API
 * - Prepares column definitions with sorting controls
 *
 * @param {object} params - Optional configuration
 * @param {Array} params.columnHandlers - Optional array of column handler functions
 * @returns {object} { setSort, columns }
 */
export function usePMXDeliveriesGridUtils({ columnHandlers = [] } = {}) {
  const { fetchPMXDeliveries, updatePMXDeliveries, pmxDeliveriesRange } =
    usePMXDeliveriesData();

  const [sortedColId, setSortedColId] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  // Handle sorting: update state, fetch sorted data, update PMX deliveries
  const setSort = useCallback(
    async (colId, direction) => {
      setSortedColId(colId);
      setSortDirection(direction);

      const customSort = [colId, direction.toUpperCase()];
      const data = await fetchPMXDeliveries(
        pmxDeliveriesRange,
        null,
        customSort,
      );

      if (data) {
        updatePMXDeliveries({
          deliveries: data?.content,
          totalPages: data?.totalPages,
          totalItems: data?.totalElements,
          currentPage: data?.pageable?.pageNumber,
          pmxDeliveriesSort: customSort,
        });
      }
    },
    [fetchPMXDeliveries, updatePMXDeliveries, pmxDeliveriesRange],
  );

  // Prepare column definitions with sorting controls
  const baseColumns = columnDefs["pmxDeliveries"](...columnHandlers)["default"];

  const columns = useMemo(
    () =>
      baseColumns.map((col) => ({
        ...col,
        headerComponentParams: {
          ...col.headerComponentParams,
          setSort,
          sortedColId,
          sortDirection,
        },
      })),
    [setSort, sortedColId, sortDirection, baseColumns],
  );

  return { setSort, columns };
}
